/**
 * PanelOferta — szybki widok oferty/usług firmy w panelu.
 * Listuje firmy usera + pakiety usług każdej firmy z linkiem do edycji w BusinessWizardV2.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Plus, Edit3, Briefcase, ArrowRight, Eye, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PanelLayout from '@/components/PanelLayout';
import { API_BASE as API } from '@/utils/api';

const PRICE_LABEL = (s) => {
  const v = s?.price || '';
  switch (s?.price_type) {
    case 'from': return v ? `od ${v} zł` : 'od ...';
    case 'hourly': return v ? `${v} zł/h` : '... zł/h';
    case 'free': return 'bezpłatne';
    case 'contact': return 'wycena indywidualna';
    default: return v ? `${v} zł` : '—';
  }
};

export default function PanelOferta() {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetch(`${API}/api/business/my`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setBusinesses(Array.isArray(data) ? data : []))
      .catch(() => toast.error('Błąd ładowania ofert'))
      .finally(() => setLoading(false));
  }, [token, navigate]);

  if (loading) {
    return (
      <PanelLayout>
        <div className="flex items-center justify-center min-h-[40vh]">
          <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      </PanelLayout>
    );
  }

  return (
    <PanelLayout>
      <div className="max-w-[1200px] mx-auto" data-testid="panel-oferta">
        <header className="mb-6 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Moja oferta</h1>
            <p className="text-sm text-gray-500 mt-1">Pakiety usług, cennik i opisy widoczne na Twojej stronie firmowej.</p>
          </div>
          <Button
            type="button"
            onClick={() => navigate(businesses[0]?.id ? `/wizard/firma-v2/${businesses[0].id}` : '/wizard/firma-v2')}
            data-testid="panel-oferta-edit-btn"
            className="bg-violet-600 hover:bg-violet-700 text-white"
          >
            <Edit3 className="w-4 h-4 mr-1.5" />
            {businesses.length > 0 ? 'Edytuj ofertę' : 'Stwórz firmę'}
          </Button>
        </header>

        {businesses.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-violet-200 bg-violet-50/30 p-10 text-center">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-white shadow-sm flex items-center justify-center mb-4">
              <Briefcase className="w-8 h-8 text-violet-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">Brak profilu firmy</h3>
            <p className="text-sm text-gray-500 mt-1 mb-5 max-w-md mx-auto">
              Utwórz profil firmy w kreatorze. Zajmie ok. 5 minut — od razu zobaczysz live preview swojej strony.
            </p>
            <Button onClick={() => navigate('/wizard/firma-v2')} className="bg-violet-600 hover:bg-violet-700">
              <Plus className="w-4 h-4 mr-1.5" /> Stwórz profil firmy
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {businesses.map((biz) => (
              <section key={biz.id} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-testid={`panel-oferta-biz-${biz.id}`}>
                <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-violet-50/40 to-white">
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-wider font-bold text-violet-600 mb-0.5">{biz.industry || 'Firma'}</p>
                    <h2 className="text-lg font-bold text-gray-900 truncate">{biz.name}</h2>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {biz.slug && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => window.open(`/firma/${biz.slug}`, '_blank')}
                        data-testid={`panel-oferta-view-${biz.id}`}
                      >
                        <Eye className="w-4 h-4 sm:mr-1.5" />
                        <span className="hidden sm:inline">Podgląd</span>
                      </Button>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => navigate(`/wizard/firma-v2/${biz.id}`)}
                      data-testid={`panel-oferta-edit-${biz.id}`}
                      className="bg-violet-600 hover:bg-violet-700"
                    >
                      <Edit3 className="w-4 h-4 sm:mr-1.5" />
                      <span className="hidden sm:inline">Edytuj</span>
                    </Button>
                  </div>
                </div>

                <div className="p-5">
                  {!biz.services || biz.services.length === 0 ? (
                    <button
                      type="button"
                      onClick={() => navigate(`/wizard/firma-v2/${biz.id}`)}
                      data-testid={`panel-oferta-add-${biz.id}`}
                      className="w-full border-2 border-dashed border-violet-200 rounded-xl py-6 px-4 text-center hover:bg-violet-50/40 transition-colors"
                    >
                      <Plus className="w-6 h-6 mx-auto text-violet-500 mb-1.5" />
                      <p className="text-sm font-semibold text-gray-900">Dodaj pierwszą usługę</p>
                      <p className="text-xs text-gray-500 mt-0.5">Klienci szukają konkretnych pakietów z cenami</p>
                    </button>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {biz.services.map((s, idx) => (
                        <div key={s.id || idx} className="rounded-xl border border-gray-200 p-3 hover:border-violet-300 hover:shadow-sm transition-all" data-testid={`panel-oferta-service-${idx}`}>
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-semibold text-gray-900 text-sm truncate">{s.name || 'Usługa'}</p>
                            <span className="px-2 py-0.5 rounded-md bg-violet-100 text-violet-700 text-[11px] font-bold whitespace-nowrap flex-shrink-0">
                              {PRICE_LABEL(s)}
                            </span>
                          </div>
                          {s.description && (
                            <p className="text-xs text-gray-500 mt-1 line-clamp-2">{s.description}</p>
                          )}
                          <div className="mt-2 flex items-center gap-1 text-[11px] text-gray-400">
                            <Tag className="w-3 h-3" /> Pakiet #{idx + 1}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="px-5 py-3 bg-gray-50/60 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                  <span>{biz.services?.length || 0} usług · {biz.portfolio?.length || biz.photos?.length || 0} realizacji · {biz.reviews?.length || 0} opinii</span>
                  <button
                    type="button"
                    onClick={() => navigate(`/wizard/firma-v2/${biz.id}`)}
                    className="text-violet-700 font-semibold hover:underline flex items-center gap-1"
                  >
                    Edytuj w kreatorze <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </PanelLayout>
  );
}
